import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Icon,
  Text,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiEye,
  FiDownload,
  FiTrash2,
  FiEdit,
} from 'react-icons/fi';

const AuditLogsTab = ({ logs, loading, onRefresh }) => {
  const [actionFilter, setActionFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Filtrar logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesAction = actionFilter === 'all' || log.action === actionFilter;
      const matchesRole = roleFilter === 'all' || log.user_role === roleFilter;
      return matchesAction && matchesRole;
    });
  }, [logs, actionFilter, roleFilter]);

  const getActionBadge = (action) => {
    const actionConfig = {
      created: { color: 'green', label: 'Creado', icon: FiEdit },
      viewed: { color: 'blue', label: 'Visto', icon: FiEye },
      downloaded: { color: 'purple', label: 'Descargado', icon: FiDownload },
      revoked: { color: 'red', label: 'Revocado', icon: FiTrash2 },
    };

    const config = actionConfig[action] || { color: 'gray', label: action, icon: FiEye };
    
    return (
      <HStack spacing={2}>
        <Icon as={config.icon} boxSize={4} />
        <Badge colorScheme={config.color}>{config.label}</Badge>
      </HStack>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      student: { color: 'blue', label: 'Estudiante' },
      instructor: { color: 'purple', label: 'Instructor' },
      admin: { color: 'orange', label: 'Administrador' },
    };

    const config = roleConfig[role] || { color: 'gray', label: role || 'N/A' };
    return <Badge colorScheme={config.color} variant="subtle">{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando logs de auditoría...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Actions */}
            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={onRefresh}
                variant="outline"
                colorScheme="blue"
              >
                Actualizar
              </Button>
              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                Total: {logs.length}
              </Badge>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                Filtrados: {filteredLogs.length}
              </Badge>
            </HStack>

            {/* Filters */}
            <HStack spacing={3} flexWrap="wrap">
              <Select
                maxW="200px"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              >
                <option value="all">Todas las acciones</option>
                <option value="created">Creados</option>
                <option value="viewed">Vistos</option>
                <option value="downloaded">Descargados</option>
                <option value="revoked">Revocados</option>
              </Select>

              <Select
                maxW="200px"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">Todos los roles</option>
                <option value="student">Estudiante</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Administrador</option>
              </Select>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Logs Table */}
      {filteredLogs.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          {logs.length === 0 
            ? 'No hay logs de auditoría registrados' 
            : 'No se encontraron logs con los filtros aplicados'}
        </Alert>
      ) : (
        <Card>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Fecha y Hora</Th>
                  <Th>Acción</Th>
                  <Th>Usuario</Th>
                  <Th>Rol</Th>
                  <Th>ID Certificado</Th>
                  <Th>Context ID</Th>
                  <Th>Detalles</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredLogs.map((log) => (
                  <Tr key={log.id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" fontWeight="medium">
                          {new Date(log.timestamp).toLocaleDateString('es-ES')}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {new Date(log.timestamp).toLocaleTimeString('es-ES')}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>{getActionBadge(log.action)}</Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {log.user_name}
                      </Text>
                    </Td>
                    <Td>{getRoleBadge(log.user_role)}</Td>
                    <Td>
                      {log.certificate_id ? (
                        <Text fontSize="xs" color="blue.600" fontFamily="mono">
                          #{log.certificate_id}
                        </Text>
                      ) : (
                        <Text fontSize="xs" color="gray.500">N/A</Text>
                      )}
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="gray.600">
                        {log.context_id || 'N/A'}
                      </Text>
                    </Td>
                    <Td>
                      {log.details ? (
                        <Text fontSize="xs" color="gray.600">
                          {JSON.stringify(log.details)}
                        </Text>
                      ) : (
                        <Text fontSize="xs" color="gray.400">-</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default AuditLogsTab;
